import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FormattedMessage } from 'react-intl'
import { BarChartOutlined, FileTextOutlined, WhatsAppOutlined } from '@ant-design/icons'
import cn from 'classnames'

import SnoozeButton from './SnoozeButton'
import { GROUP_COUNT } from './steps'
import styles from './financeOnboarding.module.scss'

const PAD = 6
const TIP_WIDTH = 340
const GAP = 16
const MASK_KEYS = ['top', 'bottom', 'left', 'right']

// Ikon & warna kartu mengikuti langkah besar: 1 tagihan, 2 WhatsApp, 3 laporan.
const GROUP_ICONS = {
  1: FileTextOutlined,
  2: WhatsAppOutlined,
  3: BarChartOutlined,
}
const GROUPS = Array.from({ length: GROUP_COUNT }, (_, i) => i + 1)

const unionRect = (target, union) => {
  const r = target.getBoundingClientRect()
  const box = { left: r.left, top: r.top, right: r.right, bottom: r.bottom }
  if (union) {
    const u = union.getBoundingClientRect()
    if (u.width > 0 && u.height > 0) {
      box.left = Math.min(box.left, u.left)
      box.top = Math.min(box.top, u.top)
      box.right = Math.max(box.right, u.right)
      box.bottom = Math.max(box.bottom, u.bottom)
    }
  }
  return {
    x: box.left - PAD,
    y: box.top - PAD,
    w: box.right - box.left + PAD * 2,
    h: box.bottom - box.top + PAD * 2,
  }
}

const canScroll = (node, axis) => {
  const style = window.getComputedStyle(node)
  const overflow = axis === 'x' ? style.overflowX : style.overflowY
  if (!/(auto|scroll|overlay)/.test(overflow)) {
    return false
  }
  if (axis === 'x') {
    return node.scrollWidth > node.clientWidth + 1
  }
  return node.scrollHeight > node.clientHeight + 1
}

const findScrollable = (el, axis) => {
  let node = el
  while (node && node !== document.body) {
    if (canScroll(node, axis)) {
      return node
    }
    node = node.parentElement
  }
  return null
}

// Cari elemen scrollable di bawah pointer supaya gulungan tetap jalan
// walau area lain sedang diblokir mask transparan. Geser samping diteruskan
// ke tabel (overflow-x), gulungan atas-bawah tetap ke halaman.
const scrollUnderPointer = (masks, clientX, clientY, deltaX, deltaY) => {
  if (!deltaX && !deltaY) {
    return
  }
  masks.forEach((m) => m.classList.add(styles.maskPassthrough))
  const el = document.elementFromPoint(clientX, clientY)
  masks.forEach((m) => m.classList.remove(styles.maskPassthrough))

  if (Math.abs(deltaX) > Math.abs(deltaY)) {
    const horizontal = findScrollable(el, 'x')
    if (horizontal) {
      horizontal.scrollLeft += deltaX
    }
    return
  }

  const vertical = findScrollable(el, 'y')
  if (vertical) {
    vertical.scrollTop += deltaY
    return
  }
  window.scrollBy(0, deltaY)
}

const Spotlight = ({
  target,
  union,
  placement = 'right',
  mask = 'block',
  group,
  eyebrow,
  sub,
  title,
  desc,
  hintId,
  warn,
  onRefuse,
  onSnooze,
  preferAbove = false,
}) => {
  const [rect, setRect] = useState(null)
  const [tipPos, setTipPos] = useState({ left: 12, top: 12, placement })
  const tipRef = useRef(null)
  const maskRefs = useRef({})

  const measure = useCallback(() => {
    if (!target || !target.isConnected) {
      setRect(null)
      return
    }
    // Garis fokus hanya mengikuti target (select). Dropdown tidak ikut
    // memperbesar garis — itu yang bikin border "tumbuh" dan tidak berhenti.
    const ring = unionRect(target, null)
    if (ring.w <= PAD * 2 && ring.h <= PAD * 2) {
      setRect(null)
      return
    }
    const hole = unionRect(target, union)
    setRect((prev) => {
      const same =
        prev &&
        prev.ring.x === ring.x &&
        prev.ring.y === ring.y &&
        prev.ring.w === ring.w &&
        prev.ring.h === ring.h &&
        prev.hole.x === hole.x &&
        prev.hole.y === hole.y &&
        prev.hole.w === hole.w &&
        prev.hole.h === hole.h
      return same ? prev : { ring, hole }
    })
  }, [target, union])

  useEffect(() => {
    measure()
    const timer = window.setInterval(measure, 150)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [measure])

  useEffect(() => {
    if (!rect) {
      return
    }
    const W = window.innerWidth
    const H = window.innerHeight
    const th = tipRef.current ? tipRef.current.offsetHeight : 0
    const anchor = rect.ring
    const narrow = W < 768
    // Di HP panel langkah menempel di bawah, header di atas. Tooltip tidak boleh
    // masuk ke dua area itu, dan tidak boleh menutup target yang harus diklik.
    const topInset = narrow ? 76 : 12
    const bottomInset = narrow ? 88 : 12
    const tipWidth = Math.min(TIP_WIDTH, W - 24)
    const spaceBelow = H - bottomInset - (anchor.y + anchor.h) - GAP
    const spaceAbove = anchor.y - topInset - GAP
    let usedPlacement = placement
    const roomRight = W - (anchor.x + anchor.w)
    const roomLeft = anchor.x
    const sideTooTight =
      (usedPlacement === 'right' && roomRight < tipWidth + GAP) ||
      (usedPlacement === 'left' && roomLeft < tipWidth + GAP)
    // Select kontak/produk/kategori membuka dropdown ke bawah. Di layar sempit
    // kartu langkah harus di atas select supaya dropdown tidak tertutup.
    const pinAbove = preferAbove && sideTooTight
    if (pinAbove) {
      usedPlacement = 'top'
    } else if (sideTooTight) {
      usedPlacement = spaceBelow >= spaceAbove ? 'bottom' : 'top'
    }
    if (!pinAbove && usedPlacement === 'bottom' && spaceBelow < th && spaceAbove > spaceBelow) {
      usedPlacement = 'top'
    } else if (!pinAbove && usedPlacement === 'top' && spaceAbove < th && spaceBelow > spaceAbove) {
      usedPlacement = 'bottom'
    }
    let left
    let top
    switch (usedPlacement) {
      case 'bottom':
        left = anchor.x
        top = anchor.y + anchor.h + GAP
        break
      case 'top':
        // Di HP kartu selebar layar. Ratakan ke kiri select supaya panah
        // tidak geser ke kanan dan terpotong tepi layar.
        left = pinAbove ? anchor.x : anchor.x + anchor.w - tipWidth
        top = anchor.y - th - GAP
        break
      case 'left':
        left = anchor.x - tipWidth - GAP
        top = anchor.y
        break
      default:
        left = anchor.x + anchor.w + GAP
        top = anchor.y
    }
    left = Math.max(12, Math.min(W - tipWidth - 12, left))
    // Jangan jepit kartu ke dalam viewport. Kalau dijepit, saat select
    // digulung kartu tertinggal di layar dan lepas dari targetnya.
    if (pinAbove && th > 0) {
      top = anchor.y - GAP - th
    }
    setTipPos({ left, top, placement: usedPlacement, pinAbove })
  }, [rect, placement, title, desc, warn, preferAbove])

  const setMaskRef = useCallback((key) => {
    return (node) => {
      maskRefs.current[key] = node
    }
  }, [])

  // Wheel harus native listener non-passive supaya preventDefault jalan.
  useEffect(() => {
    if (mask !== 'block') {
      return undefined
    }
    const nodes = MASK_KEYS.map((k) => maskRefs.current[k]).filter(Boolean)
    const handler = (event) => {
      event.preventDefault()
      scrollUnderPointer(nodes, event.clientX, event.clientY, event.deltaX, event.deltaY)
    }
    // Geser jari harus ikut menggulir konten di bawah mask, sama seperti roda mouse.
    // Termasuk geser samping pada tabel laporan yang kolomnya lebih lebar dari layar.
    let lastX = 0
    let lastY = 0
    const onTouchStart = (event) => {
      lastX = event.touches[0]?.clientX || 0
      lastY = event.touches[0]?.clientY || 0
    }
    const onTouchMove = (event) => {
      const touch = event.touches[0]
      if (!touch) {
        return
      }
      event.preventDefault()
      const deltaX = lastX - touch.clientX
      const deltaY = lastY - touch.clientY
      lastX = touch.clientX
      lastY = touch.clientY
      scrollUnderPointer(nodes, touch.clientX, touch.clientY, deltaX, deltaY)
    }
    nodes.forEach((n) => {
      n.addEventListener('wheel', handler, { passive: false })
      n.addEventListener('touchstart', onTouchStart, { passive: true })
      n.addEventListener('touchmove', onTouchMove, { passive: false })
    })
    return () => {
      nodes.forEach((n) => {
        n.removeEventListener('wheel', handler)
        n.removeEventListener('touchstart', onTouchStart)
        n.removeEventListener('touchmove', onTouchMove)
      })
    }
  }, [mask, rect])

  const handleRefuse = useCallback(() => {
    if (onRefuse) {
      onRefuse()
    }
  }, [onRefuse])

  if (!rect) {
    return null
  }

  const W = window.innerWidth
  const H = window.innerHeight
  const { hole, ring } = rect
  const maskRects = {
    top: { left: 0, top: 0, width: W, height: Math.max(0, hole.y) },
    bottom: {
      left: 0,
      top: hole.y + hole.h,
      width: W,
      height: Math.max(0, H - hole.y - hole.h),
    },
    left: { left: 0, top: hole.y, width: Math.max(0, hole.x), height: hole.h },
    right: {
      left: hole.x + hole.w,
      top: hole.y,
      width: Math.max(0, W - hole.x - hole.w),
      height: hole.h,
    },
  }
  const arrowClass = {
    right: styles.arrowRight,
    left: styles.arrowLeft,
    bottom: styles.arrowBottom,
    top: styles.arrowTop,
  }[tipPos.placement || placement]
  const GroupIcon = GROUP_ICONS[group]

  return createPortal(
    <>
      {mask === 'block' &&
        MASK_KEYS.map((key) => (
          <div
            key={key}
            ref={setMaskRef(key)}
            className={styles.mask}
            style={maskRects[key]}
            onClick={handleRefuse}
            aria-hidden="true"
          />
        ))}
      <div
        className={styles.ring}
        style={{ left: ring.x, top: ring.y, width: ring.w, height: ring.h }}
        aria-hidden="true"
      />
      {title && (
        <div
          ref={tipRef}
          className={cn(styles.tip, warn && styles.tipShake)}
          style={tipPos}
          role="dialog"
          aria-live="polite"
        >
          <span className={styles.tipGlass} aria-hidden="true" />
          <span className={cn(styles.arrow, arrowClass, tipPos.pinAbove && styles.arrowPinLeft)} />
          <div className={styles.tipHead}>
            {GroupIcon && (
              <span className={cn(styles.tipIcon, styles[`tipIcon${group}`])}>
                <GroupIcon />
              </span>
            )}
            <span className={styles.tipMeta}>
              {eyebrow && <span className={styles.tipEyebrow}>{eyebrow}</span>}
              {sub && <span className={styles.tipSub}>{sub}</span>}
            </span>
          </div>
          <h4>{title}</h4>
          <p>{desc}</p>
          <div className={styles.tipCue}>
            <span className={styles.tipCueDot} />
            <FormattedMessage id={hintId || 'financeOnboarding.hint_click'} />
          </div>
          {warn && (
            <div className={styles.warn}>
              <FormattedMessage id="financeOnboarding.locked" />
            </div>
          )}
          <div className={styles.tipFoot}>
            {onSnooze ? <SnoozeButton onClick={onSnooze} inline /> : <span />}
            <span className={styles.tipDots} aria-hidden="true">
              {GROUPS.map((n) => (
                <i
                  key={n}
                  className={cn(n === group && styles.tipDotActive, n < group && styles.tipDotDone)}
                />
              ))}
            </span>
          </div>
        </div>
      )}
    </>,
    document.body,
  )
}

export default Spotlight
